import { Box } from '@abyss/web/ui/Box';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { IconAlignmentContainer } from '../../../../common/ProviderDetailTabs/ProviderLocationsDetails/ProviderLocation.style';
import { isUmrMember } from '../../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../../context/PortalContext';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import {
  convertTimeToAmPm,
  filterNotEmptyHoursOfOperations,
  getUncommonElements,
  groupingClosingAndOpeningTime,
} from '../../../../utils/providerDetails.utils';
import {
  DividerStyle,
  HoursOfOperationFlex,
  LayoutGroupStyled,
} from './CompareSmartChoice.styled';
import { isAttributeChecked } from './utils';

export const Convenience = ({
  selectedAttributes,
  preferencesData,
  filteredProviders,
}) => {
  const { t } = useTranslation();
  const lagoonData = useLagoon('ui-messaging')();
  const isSmartChoice = useSmartChoice();
  const opsHours = (provider) => {
    if (!provider?.hoursOfOperation) {
      return [];
    }

    // Format 1: Array format
    if (Array.isArray(provider?.hoursOfOperation)) {
      return provider?.hoursOfOperation.map((item) => ({
        day: item?.day,
        openingTime: item?.openingTime || '',
        closingTime: item?.closingTime || '',
      }));
    }

    // Format 2: Object format
    if (typeof provider?.hoursOfOperation === 'object') {
      return Object.keys(provider?.hoursOfOperation).map((day) => {
        const timeString = provider?.hoursOfOperation[day];
        const times = timeString?.split(',') || [];

        return {
          day,
          openingTime: times[0]?.trim() || '',
          closingTime: times[1]?.trim() || '',
        };
      });
    }

    return [];
  };

  const notEmptyHoursOfOperation = (provider) =>
    filterNotEmptyHoursOfOperations({
      ...provider,
      hoursOfOperation: opsHours(provider),
    });

  const groupByClosingAndOpeningTime = (provider) =>
    groupingClosingAndOpeningTime(t, notEmptyHoursOfOperation(provider));

  const notAvailableDays = (provider) =>
    getUncommonElements(
      Constants.ALL_DAYS,
      notEmptyHoursOfOperation(provider).map((days) => days.day)
    );
  const translationOfNotAvailableDays = (provider) =>
    notAvailableDays(provider)?.map((day) => t(day));
  const accept_new_patients = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_ACCEPCTING_NEW_PATIENTS,
  })?.message;
  const not_accepting_new_patients = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_NOT_ACCEPTING_NEW_PATIENTS,
  })?.message;
  const not_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_AVAILABLE_TEXT,
  })?.message;
  const evening_appointments_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_EVENING_APPONTMENTS_AVAILABILE,
  })?.message;
  const evening_appointments_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_EVENING_APPONTMENTS_UNAVAILABILE,
  })?.message;
  const weekend_appointments_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_WEEKEND_APPONTMENTS_AVAILABILE,
  })?.message;
  const weekend_appointments_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_WEEKEND_APPONTMENTS_UNAVAILABILE,
  })?.message;
  const online_appointment_scheduling_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_ONLINE_APOINTMENT_SCHEDULE_AVAILABLE_THOUGHT_MYUHC_COM,
  })?.message;
  const online_appointment_scheduling_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_ONLINE_APOINTMENT_SCHEDULE_UNAVAILABLE_THOUGHT_MYUHC_COM,
  })?.message;
  const virtual_appointments_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_VIRTUAL_APOINTMENTS_AVAILABLE,
  })?.message;
  const virtual_appointments_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_VIRTUAL_APOINTMENTS_UNAVAILABLE,
  })?.message;
  const offers_virtual_Care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_OFFERS_VIRTUAL_CARE,
  })?.message;
  const no_virtual_Care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NO_VIRTUAL_CARE,
  })?.message;

  const renderOpenCloseHours = (groupedHours, open) =>
    Object.keys(groupedHours[open])?.map((close) => (
      <HoursOfOperationFlex key={`${open}-${close}`}>
        <Text fontWeight="$semibold">
          {groupedHours[open][close].map(
            (days, index) => (index ? ', ' : '') + days?.slice(0, 2)
          )}
          :
        </Text>
        <Text data-testid="available-days-provider">
          {convertTimeToAmPm(open.slice(0, 5))} -{' '}
          {convertTimeToAmPm(close.slice(0, 5))}
        </Text>
      </HoursOfOperationFlex>
    ));

  const renderGroupedHours = (groupedHours) =>
    Object.keys(groupedHours)?.map((open) => (
      <div key={`${open}`}>{renderOpenCloseHours(groupedHours, open)}</div>
    ));
  const renderHoursOfOperation = (provider) => {
    const groupedHours = groupByClosingAndOpeningTime(provider);
    const notAvailableDays = translationOfNotAvailableDays(provider);

    return (
      <div>
        {renderGroupedHours(groupedHours)}
        {notAvailableDays?.length > 0 && (
          <HoursOfOperationFlex>
            <Text fontWeight="$semibold">
              {notAvailableDays.map(
                (DaysNotAvailable, index) =>
                  (index ? ', ' : '') + t(DaysNotAvailable?.slice(0, 2))
              )}
              :
            </Text>
            <Text data-testid="available-days-provider">{not_available}</Text>
          </HoursOfOperationFlex>
        )}
      </div>
    );
  };

  const ProviderHours = ({ provider }) => {
    const hours = opsHours(provider);

    return hours?.length ? (
      <IconAlignmentContainer css={{ alignItems: 'flex-start' }}>
        {renderHoursOfOperation(provider)}
      </IconAlignmentContainer>
    ) : (
      <IconAlignmentContainer>
        <Text fontWeight="$semibold">{not_available}</Text>
      </IconAlignmentContainer>
    );
  };
  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  return (
    <React.Fragment>
      {selectedAttributes?.find((item) => item?.name === 'acceptingNewPatients')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                data-testid="accepting-new-patients"
                key={index}
              >
                <>
                  {provider?.acceptingNewPatients ? (
                    <>
                      <IconSymbol color="#007000" icon="done" size={20} />
                      <Text css={{ marginLeft: '8px' }}>
                        {accept_new_patients}
                      </Text>
                    </>
                  ) : (
                    <Text>{not_accepting_new_patients}</Text>
                  )}
                </>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {isAttributeChecked(selectedAttributes, [
            'appointmentAvailability',
            'hoursOfOperation',
            'hasEveningAppointments',
            'hasWeekendAppointment',
            'onlineAppointmentScheduling',
            'virtualCareOffered',
            'distance',
            'languageSpoken',
            'languagesSpoken',
            'providerEthnicityExpress',
            'eapAccepted',
            'practiceLimitations',
            'gender',
            'boardCertified',
            'reviews',
            'servicesOffered',
          ]) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {selectedAttributes?.find(
        (item) => item?.name === 'appointmentAvailability'
      )?.isChecked &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  alignItems="top"
                  css={{ minHeight: '110px' }}
                  data-testid="provider-ops-hours"
                  key={index}
                >
                  <ProviderHours provider={provider} />
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>

            {isAttributeChecked(selectedAttributes, [
              'hasEveningAppointments',
              'hasWeekendAppointment',
              'onlineAppointmentScheduling',
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'gender',
              'boardCertified',
              'reviews',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'hoursOfOperation')
        ?.isChecked &&
        !isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  alignItems="top"
                  css={{ minHeight: '110px' }}
                  data-testid="provider-ops-hours"
                  key={index}
                >
                  <ProviderHours provider={provider} />
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            {isAttributeChecked(selectedAttributes, [
              'hasEveningAppointments',
              'hasWeekendAppointment',
              'onlineAppointmentScheduling',
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'gender',
              'boardCertified',
              'reviews',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find(
        (item) => item?.name === 'hasEveningAppointments'
      )?.isChecked &&
        ((isSmartChoice && preferencesData?.evngApptAvbl === 'y') ||
          !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  data-testid="provider-evening-appointments"
                  key={index}
                >
                  <>
                    {provider?.hasEveningAppointments ? (
                      <>
                        <IconSymbol color="#007000" icon="done" size={20} />
                        <Text css={{ marginLeft: '8px' }}>
                          {evening_appointments_available}
                        </Text>
                      </>
                    ) : (
                      <Text>{evening_appointments_unavailable}</Text>
                    )}
                  </>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            {isAttributeChecked(selectedAttributes, [
              'hasWeekendAppointment',
              'onlineAppointmentScheduling',
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'gender',
              'boardCertified',
              'reviews',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find(
        (item) => item?.name === 'hasWeekendAppointment'
      )?.isChecked &&
        ((isSmartChoice && preferencesData?.wkndApptAvbl === 'y') ||
          !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  data-testid="provider-weekend-appointments"
                  key={index}
                >
                  <>
                    {provider?.hasWeekendAppointment ? (
                      <>
                        <IconSymbol color="#007000" icon="done" size={20} />
                        <Text css={{ marginLeft: '8px' }}>
                          {weekend_appointments_available}
                        </Text>
                      </>
                    ) : (
                      <Text>{weekend_appointments_unavailable}</Text>
                    )}
                  </>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>

            {isAttributeChecked(selectedAttributes, [
              'onlineAppointmentScheduling',
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'gender',
              'boardCertified',
              'reviews',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {!isUmr &&
        selectedAttributes?.find(
          (item) => item?.name === 'onlineAppointmentScheduling'
        )?.isChecked &&
        ((isSmartChoice &&
          preferencesData?.onlineApptSchdlAvbl?.toLowerCase() === 'y') ||
          !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  data-testid="provider-online-scheduling"
                  key={index}
                >
                  <>
                    {provider?.recommendationDetails?.convenience
                      ?.onlineAppointmentScheduling === 'Y' ? (
                      <>
                        <IconSymbol color="#007000" icon="done" size={20} />
                        <Text css={{ marginLeft: '8px' }}>
                          {online_appointment_scheduling_available}
                        </Text>
                      </>
                    ) : (
                      <Text>{online_appointment_scheduling_unavailable}</Text>
                    )}
                  </>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>

            {isAttributeChecked(selectedAttributes, [
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'gender',
              'boardCertified',
              'reviews',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'virtualCareOffered')
        ?.isChecked &&
        ((isSmartChoice && preferencesData?.virtualApptAvbl === 'y') ||
          !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  data-testid="provider-offers-virtual-visits"
                  key={index}
                >
                  {isSmartChoice ? (
                    <>
                      {provider?.recommendationDetails?.convenience
                        ?.virtualAppointments === 'Y' ? (
                        <>
                          <IconSymbol color="#007000" icon="done" size={20} />
                          <Text css={{ marginLeft: '8px' }}>
                            {virtual_appointments_available}
                          </Text>
                        </>
                      ) : (
                        <Text>{virtual_appointments_unavailable}</Text>
                      )}
                    </>
                  ) : (
                    <>
                      {provider?.virtualCareOffered?.length ? (
                        <>
                          <IconSymbol color="#007000" icon="done" size={20} />
                          <Text css={{ marginLeft: '8px' }}>
                            {offers_virtual_Care}
                          </Text>
                        </>
                      ) : (
                        <Text>{no_virtual_Care}</Text>
                      )}
                    </>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>

            {isSmartChoice &&
              isAttributeChecked(selectedAttributes, ['distance']) && (
                <LayoutGroupStyled
                  css={{ '& > div': { width: '100%' } }}
                  cssProps={{ minHeightAuto: true }}
                  grow
                >
                  {filteredProviders?.map((_, index) => (
                    <React.Fragment key={index}>
                      <DividerStyle />
                    </React.Fragment>
                  ))}
                </LayoutGroupStyled>
              )}
            {!isSmartChoice &&
              isAttributeChecked(selectedAttributes, [
                'distance',
                'languageSpoken',
                'languagesSpoken',
                'providerEthnicityExpress',
                'eapAccepted',
                'practiceLimitations',
                'gender',
                'boardCertified',
                'reviews',
                'servicesOffered',
              ]) && (
                <LayoutGroupStyled
                  css={{ '& > div': { width: '100%' } }}
                  cssProps={{ minHeightAuto: true }}
                  grow
                >
                  {filteredProviders?.map((_, index) => (
                    <React.Fragment key={index}>
                      <DividerStyle />
                    </React.Fragment>
                  ))}
                </LayoutGroupStyled>
              )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'distance')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                css={{ minHeight: '80px', alignItems: 'flex-start' }}
                data-testid="provider-address-miles"
                key={index}
              >
                <Box color="$white" css={{ padding: '0px' }}>
                  <Text>
                    {provider?.address?.line[0]}, {provider?.address?.city},{' '}
                    {provider?.address?.state}
                  </Text>
                  <Flex css={{ marginTop: 8 }}>
                    <IconSymbol
                      color="$gray7"
                      icon="directions"
                      size={20}
                      style={{ marginRight: 4 }}
                      variant="outlined"
                    />
                    <Text>{parseFloat(provider?.distance)?.toFixed(2)} mi</Text>
                  </Flex>
                </Box>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {!isSmartChoice &&
            isAttributeChecked(selectedAttributes, [
              'languageSpoken',
              'languagesSpoken',
              'gender',
              'boardCertified',
              'reviews',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
        </>
      )}
    </React.Fragment>
  );
};
